import {inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';
import {forkJoin, take} from 'rxjs';
import {ApiService} from './api.service';
import {NotificationChannel} from '../types/notifications-types/notificationChannel';
import {NotificationReadState} from '../types/notifications-types/notificationReadState';
import {NotificationResource} from '../types/notifications-types/notificationResource';

type NotificationErrorHandler = (error: unknown) => void;

@Injectable({
  providedIn: 'root'
})
export class NotificationStateService {
  private apiService: ApiService = inject(ApiService);

  private _iconsBadges: WritableSignal<Map<string, boolean>> = signal(new Map<string, boolean>());
  private _barTopNotifications: WritableSignal<NotificationResource[]> = signal<NotificationResource[]>([]);
  private _latestNotifications: WritableSignal<NotificationResource[]> = signal<NotificationResource[]>([]);
  private lastBarTopQuery: {
    state: NotificationReadState;
    channels: NotificationChannel[];
    limit: number;
  } | null = null;
  private lastLatestUnreadLimit: number = 25;
  private hasLoadedLatestUnread: boolean = false;
  private isLoadingBadges: boolean = false;

  public readonly iconsBadges: Signal<Map<string, boolean>> = this._iconsBadges.asReadonly();
  public readonly barTopNotifications: Signal<NotificationResource[]> = this._barTopNotifications.asReadonly();
  public readonly latestNotifications: Signal<NotificationResource[]> = this._latestNotifications.asReadonly();

  public resetState(): void {
    this._iconsBadges.set(new Map<string, boolean>());
    this._barTopNotifications.set([]);
    this._latestNotifications.set([]);
    this.lastBarTopQuery = null;
    this.lastLatestUnreadLimit = 25;
    this.hasLoadedLatestUnread = false;
    // Note: isLoadingBadges is intentionally NOT reset here to prevent race conditions
    // when resetState() is called immediately before loadNotificationBadges()
  }

  public loadNotificationBadges(onError?: NotificationErrorHandler): void {
    // Prevent concurrent requests
    if (this.isLoadingBadges) {
      return;
    }

    this.isLoadingBadges = true;
    this.apiService.setNotificationBadges()
      .pipe(take(1))
      .subscribe({
        next: (notificationBadges) => {
          this._iconsBadges.set(notificationBadges);
          this.isLoadingBadges = false;
        },
        error: (error) => {
          this.isLoadingBadges = false;
          onError?.(error);
        }
      });
  }

  public hasNonChatBadgeAlert(): boolean {
    for (const [badgeKey, hasAlert] of this._iconsBadges().entries()) {
      if (badgeKey !== NotificationChannel.Chat && hasAlert) {
        return true;
      }
    }
    return false;
  }

  public clearBarTopNotifications(): void {
    this._barTopNotifications.set([]);
  }

  public loadBarTopNotifications(
    state: NotificationReadState,
    channels: NotificationChannel[],
    append: boolean = true,
    limit: number = 25,
    onError?: NotificationErrorHandler
  ): void {
    this.lastBarTopQuery = {
      state,
      channels: [...channels],
      limit
    };

    const after = append ? this._barTopNotifications().length : 0;

    this.apiService.getNotificationsList(limit, after, state, channels)
      .pipe(take(1))
      .subscribe({
        next: (items: NotificationResource[]) => {
          if (append) {
            this._barTopNotifications.update((existing) => this.mergeNotifications(existing, items));
            return;
          }

          this._barTopNotifications.set(items);
        },
        error: (error) => {
          onError?.(error);
        }
      });
  }

  public loadLatestUnreadNotifications(limit: number = 25, onError?: NotificationErrorHandler): void {
    this.lastLatestUnreadLimit = limit;
    this.hasLoadedLatestUnread = true;

    this.apiService.getNotificationsList(limit, 0, NotificationReadState.Unread, [])
      .pipe(take(1))
      .subscribe({
        next: (items: NotificationResource[]) => {
          this._latestNotifications.set(items.filter((item) => item.read !== true));
        },
        error: (error) => {
          onError?.(error);
        }
      });
  }

  public handleSocketNotificationCreate(data: {id: number}): void {
    if (!data?.id) {
      return;
    }

    this.loadNotificationBadges();
    this.refreshLatestUnreadAfterSocketUpdate();
    this.refreshBarTopAfterSocketUpdate(true);
  }

  public handleSocketNotificationUpdate(data?: {id: number}): void {
    this.loadNotificationBadges();
    this.refreshLatestUnreadAfterSocketUpdate();
    this.refreshBarTopAfterSocketUpdate(false, data?.id);
  }

  public markNotificationRead(notificationId: number, onError?: NotificationErrorHandler): void {
    this.apiService.setNotificationRead(notificationId)
      .pipe(take(1))
      .subscribe({
        next: (notification: NotificationResource) => {
          this.removeNotificationById(notification.id);
          this.loadNotificationBadges();
        },
        error: (error) => {
          onError?.(error);
        }
      });
  }

  public markNotificationUnread(notificationId: number, onError?: NotificationErrorHandler): void {
    this.apiService.setNotificationUnread(notificationId)
      .pipe(take(1))
      .subscribe({
        next: (notification: NotificationResource) => {
          this._barTopNotifications.update((items) => items.filter((item) => item.id !== notification.id));
          this._latestNotifications.update((items) => this.mergeNotifications([notification], items));
          this.loadNotificationBadges();
        },
        error: (error) => {
          onError?.(error);
        }
      });
  }

  public setAllReadForChannels(
    channels: NotificationChannel[],
    onSuccess?: () => void,
    onError?: NotificationErrorHandler
  ): void {
    this.apiService.setNotificationsReadForChannels(channels)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this._latestNotifications.update((items) => {
            if (channels.length === 0) {
              return [];
            }
            return items.filter((item) => !channels.includes(item.channel));
          });
          this.loadNotificationBadges();
          onSuccess?.();
        },
        error: (error) => {
          onError?.(error);
        }
      });
  }

  public setAllUnreadByIds(
    notificationIds: number[],
    onSuccess?: () => void,
    onError?: NotificationErrorHandler
  ): void {
    if (notificationIds.length === 0) {
      onSuccess?.();
      return;
    }

    forkJoin(notificationIds.map((id) => this.apiService.setNotificationUnread(id).pipe(take(1))))
      .pipe(take(1))
      .subscribe({
        next: (notifications: NotificationResource[]) => {
          const ids = new Set(notificationIds);
          this._barTopNotifications.update((items) => items.filter((item) => !ids.has(item.id)));
          this._latestNotifications.update((items) => this.mergeNotifications(notifications, items));
          this.loadNotificationBadges();
          onSuccess?.();
        },
        error: (error) => {
          onError?.(error);
        }
      });
  }

  private removeNotificationById(id: number): void {
    this._barTopNotifications.update((items) => items.filter((item) => item.id !== id));
    this._latestNotifications.update((items) => items.filter((item) => item.id !== id));
  }

  private refreshLatestUnreadAfterSocketUpdate(): void {
    if (!this.hasLoadedLatestUnread) {
      return;
    }

    this.loadLatestUnreadNotifications(this.lastLatestUnreadLimit);
  }

  private refreshBarTopAfterSocketUpdate(onlyUnreadOnCreate: boolean, updatedId?: number): void {
    if (!this.lastBarTopQuery) {
      return;
    }

    if (onlyUnreadOnCreate && this.lastBarTopQuery.state !== NotificationReadState.Unread) {
      return;
    }

    const currentItems = this._barTopNotifications();
    if (updatedId && this.lastBarTopQuery.state === NotificationReadState.Unread) {
      this._barTopNotifications.set(currentItems.filter((item) => item.id !== updatedId));
    }

    const refreshLimit = Math.max(currentItems.length, this.lastBarTopQuery.limit);
    this.loadBarTopNotifications(
      this.lastBarTopQuery.state,
      this.lastBarTopQuery.channels,
      false,
      refreshLimit
    );
  }

  private mergeNotifications(existing: NotificationResource[], incoming: NotificationResource[]): NotificationResource[] {
    if (incoming.length === 0) {
      return existing;
    }

    const existingIds = new Set<number>(existing.map((item) => item.id));
    const uniqueIncoming = incoming.filter((item) => !existingIds.has(item.id));
    return [...existing, ...uniqueIncoming];
  }
}
